import React, { useCallback, useEffect, useState } from 'react';
import * as Styles from './EmailVerify.styles';
import HgOrangeLogo from '@/media/logo/HgOrangeLogo/HgOrangeLogo';
import { EmailSteps, emailErrors } from './EmailVerify.types';
import { ChangeEmail } from './components/ChangeEmail';
import useLocale from '@/hooks/useLocale/useLocale';
import { FieldValidators } from '@/utils/FieldValidators';
import { useDispatch, useSelector } from 'react-redux';
import { changeEmailVerify, emailChangeFlow, resendEmailVerify } from '@/redux/actions/emailVerify';
import {
  RECEIVE_CHANGE_EMAIL, ERROR_CHANGE_EMAIL, RECEIVE_RESEND_EMAIL, ERROR_RESEND_EMAIL,
} from '@/redux/actions/actionsTypes';
import { commonActions, formsActions } from '@/redux/modules';
import { differenceInSeconds, format } from 'date-fns';
import { COUNTRY } from '@/config';
import { changeEmailTagManager, emailVerifyResendLinkTagManager, emailVerifySkipFlowTagManager } from '@/utils/ThirdParties/tagManager';
import { SentEmail } from './components/SentEmail';
import { ValidateEmail } from './components/ValidateEmail';
import { IconButton } from 'gatorcomponents';
import { emailFlowSkip } from '@/redux/actions/summary';
import { useHistory, useParams } from 'react-router';
import { getServerTime } from '@/utils/getServerTime/getServerTime';

const changeMailInitialState = {
  email: {
    value: '',
    error: '',
  },
  checkEmail: {
    value: '',
    error: '',
  },
  password: {
    value: '',
    error: '',
  },
};

const EmailVerify = ({
  forceScreen = null,
}) => {
  const [blockedUntil, setBlockedUntil] = useState(null);
  const [blockedTime, setBlockedTime] = useState(0);
  const [timer, setTimer] = useState('0:00');

  const params = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const { changeFlow } = params;

  const {
    emailVerify: emailVerifyLocale,
    ticket: ticketsLocale,
    routes: routesLocale,
  } = useLocale();
  const {
    changeMail: changeEmailLocale,
    sentEmail: sentEmailLocale,
    validateEmail: validateEmailLocale,
  } = emailVerifyLocale;

  const clientInfo = useSelector(state => state.summary);
  const forceUrl = useSelector(state => state.emailVerify.forceUrl);
  const { loadingChange, loadingResend } = useSelector(state => state.emailVerify);
  const featureToggleEmailNumberFlowSkip = useSelector(state => state.featureToggles.toggles.portal.emailFlowSkip);

  const initialStep = () => {
    if (forceScreen) {
      return forceScreen;
    }

    return changeFlow
      ? EmailSteps.CHANGE_EMAIL
      : EmailSteps.VALIDATE_EMAIL;
  };

  const [currentStep, setCurrentStep] = useState(initialStep());
  const [changeEmailForm, setChangeEmailForm] = useState(changeMailInitialState);

  let emailValidationRequested = localStorage.getItem('emailValidationRequested');
  let emailValidationRequestedDiff = 0;

  if (emailValidationRequested) {
    emailValidationRequestedDiff = differenceInSeconds(new Date(emailValidationRequested), new Date());
  }
  if (emailValidationRequestedDiff <= 0) {
    emailValidationRequested = null;
    localStorage.removeItem('emailValidationRequested');
  }

  const [countDownResend, setCountDownResend] = useState(emailValidationRequested
    ? emailValidationRequestedDiff
    : 0);

  const closeEmailFlow = useCallback(() => {
    dispatch(emailFlowSkip(clientInfo.id));
    dispatch(emailChangeFlow(false));
    emailVerifySkipFlowTagManager();
  }, [dispatch, clientInfo]);

  const openForm = useCallback(() => {
    dispatch(formsActions.forceState.set({
      key: ticketsLocale.forms.names.withoutEmailAccess,
      params: {
        selectedProduct: ticketsLocale.forms.namewithoutEmailAccess,
      },
    }));
    closeEmailFlow();
    history.push(`${routesLocale.help}${routesLocale.supportforms}`);
  }, [dispatch, history, routesLocale, ticketsLocale, closeEmailFlow]);

  const requestEmailChange = useCallback(() => {
    dispatch(changeEmailVerify({
      clientId: clientInfo.id,
      clientEmailCurrent: clientInfo.email,
      clientNewEmail: changeEmailForm.email.value,
      password: changeEmailForm.password.value,
    })).then((response) => {
      let isPasswordError = false;
      let emailIsInUse = false;
      let isSameMail = false;
      const hasUnblockTime = false;
      const hasPendingAttempts = false;

      switch (response.type) {
        case RECEIVE_CHANGE_EMAIL:
          if (!hasPendingAttempts) {
            dispatch(commonActions.notifications.set({
              label: (
                `${emailVerifyLocale.feedback.changeSuccess}`
              ),
              type: 'success',
            }));
          }
          setCurrentStep(EmailSteps.SENT_EMAIL);
          changeEmailTagManager();
          break;
        case ERROR_CHANGE_EMAIL:
          if (response && response.info.response.data.data.disabled_til) {
            const disabledTil = new Date(response.info.response.data.data.disabled_til).getTime();
            localStorage.setItem('sendEmailCodeDisabledTil', disabledTil);
            setBlockedUntil(disabledTil);
          }
          if (response && response.info && response.info.response && response.info.response.data.internal_code === emailErrors.DATA_IN_USE) {
            isSameMail = true;
            dispatch(commonActions.notifications.set({
              label: (
                `${emailVerifyLocale.feedback.dataInUse}`
              ),
              type: 'error',
            }));
          }

          if (response && response.info && response.info.response.data.data === 'Password dont match') {
            isPasswordError = true;
            dispatch(commonActions.notifications.set({
              label: (
                `${emailVerifyLocale.feedback.password}`
              ),
              type: 'error',
            }));
          }

          if (response && response.info && response.info.response.data.data === 'Email already in use.') {
            emailIsInUse = true;
            dispatch(commonActions.notifications.set({
              label: (
                `${emailVerifyLocale.feedback.emailAlreadyRegistered}`
              ),
              type: 'error',
            }));
          }

          if (!isSameMail && !isPasswordError && !hasUnblockTime && !emailIsInUse) {
            dispatch(commonActions.notifications.set({
              label: (
                `${emailVerifyLocale.feedback.changeError}`
              ),
              type: 'error',
            }));
          }
          break;
        default: break;
      }
    });
  }, [changeEmailForm, clientInfo, dispatch, emailVerifyLocale]);

  const setPreviousBlockedTime = useCallback(() => {
    const timestamp = blockedUntil / 1000;
    const currentTimeZone = new Date().toLocaleString('en', { timeZone: 'America/Sao_Paulo' });
    const fomattedCurrentTime = format(new Date(currentTimeZone), 'yyyy-MM-dd HH:mm:ss');

    const currentTime = new Date(fomattedCurrentTime).getTime();
    const currentTimeStamp = Math.floor(currentTime / 1000);

    if (timestamp > currentTimeStamp) {
      setBlockedTime(timestamp - currentTimeStamp);
    }
  }, [blockedUntil]);

  const getBlockedUntilOnInit = useCallback(() => {
    const sendEmailDisabledTil = localStorage.getItem('sendEmailCodeDisabledTil');
    setBlockedUntil(sendEmailDisabledTil);

    const { timestamp } = getServerTime();

    if (sendEmailDisabledTil <= timestamp) {
      localStorage.removeItem('sendEmailCodeDisabledTil');
    }
  }, []);

  useEffect(() => {
    setPreviousBlockedTime();
  }, [setPreviousBlockedTime]);

  useEffect(() => {
    getBlockedUntilOnInit();
  }, [getBlockedUntilOnInit]);

  const updateBlockedTime = useCallback(() => {
    const secondsRemaining = blockedTime;

    if (secondsRemaining <= 0) return;

    const intervalId = setTimeout(() => {
      setBlockedTime(secondsRemaining - 1);
    }, 1000);

    const differenceInMs = blockedTime * 1000;
    const minutes = Math.floor((differenceInMs / 1000 / 60) % 60);
    const seconds = Math.floor((differenceInMs / 1000) % 60);

    const numberWithZero = value => String(value).padStart(2, '0');
    const formatedMinutes = numberWithZero(minutes);
    const formatedSeconds = numberWithZero(seconds);

    const formatedTime = `${formatedMinutes}:${formatedSeconds}`;
    setTimer(formatedTime);

    return () => clearTimeout(intervalId);
  }, [blockedTime]);

  useEffect(() => {
    updateBlockedTime();
  }, [updateBlockedTime]);

  const handleResendEmail = useCallback(() => {
    if (countDownResend <= 0) {
      dispatch(resendEmailVerify({
        clientId: clientInfo.id,
        clientEmail: clientInfo.email,
      })).then((response) => {
        switch (response.type) {
          case RECEIVE_RESEND_EMAIL:
            if (response.client.data && response.client.data.pending_attempts >= 0) {
              const attempts = response.client.data.pending_attempts;
              dispatch(commonActions.notifications.set({
                label: (
                  <p>
                    {emailVerifyLocale.feedback.emailSendAttempts({ attempts, used: COUNTRY === 'br' ? 10 - attempts : 5 - attempts })}
                  </p>
                ),
                type: 'success',
              }));
            } else {
              dispatch(commonActions.notifications.set({
                label: emailVerifyLocale.feedback.resendSuccess,
                type: 'success',
              }));
            }
            emailVerifyResendLinkTagManager();
            break;
          case ERROR_RESEND_EMAIL:
            if (response.client.data && response.client.data.disabled_til) {
              const disabledTil = new Date(response.client.data.disabled_til).getTime();
              localStorage.setItem('sendEmailCodeDisabledTil', disabledTil);
              setBlockedUntil(disabledTil);

              dispatch(commonActions.notifications.set({
                label: (
                  <p>
                    {emailVerifyLocale.feedback.blockedUntil}
                  </p>
                ),
                type: 'error',
              }));
              break;
            }
            if (response.client.data && response.client.data.unblock_time) {
              const unblock = new Date(response.client.data.unblock_time);
              dispatch(commonActions.notifications.set({
                label: (
                  <p>
                    {emailVerifyLocale.feedback.noMoreAttempts({ hours: format(unblock, 'HH:mm'), date: format(unblock, 'dd/MM/yyyy') })}
                  </p>
                ),
                type: 'error',
              }));
              break;
            }
            dispatch(commonActions.notifications.set({
              label: emailVerifyLocale.feedback.resendError,
              type: 'error',
            }));
            break;
          default: break;
        }
      });
    }
  }, [countDownResend, clientInfo, dispatch, emailVerifyLocale]);

  const getValidateEmailProps = useCallback(() => {
    const handleChangeEmail = () => {
      setCurrentStep(EmailSteps.CHANGE_EMAIL);
    };

    return ({
      emailVerifyLocale,
      validateEmailLocale,
      blockedTime,
      clientInfo,
      countDownResend,
      setCountDownResend,
      handleResendEmail,
      loadingResend,
      handleChangeEmail,
      openForm,
      timer,
    });
  }, [
    emailVerifyLocale,
    validateEmailLocale,
    clientInfo,
    countDownResend,
    setCountDownResend,
    handleResendEmail,
    blockedTime,
    loadingResend,
    openForm,
    timer,
  ]);

  const handleGoBack = useCallback(() => {
    if (history.length) {
      dispatch(emailChangeFlow(false, forceUrl));
      history.push(forceUrl);
    }

    setCurrentStep(EmailSteps.VALIDATE_EMAIL);
  }, [dispatch, forceUrl, history]);

  const getChangeEmailProps = useCallback(() => {
    const validateFields = () => {
      const newFormValue = { ...changeEmailForm };
      let hasErrors = false;

      const hasInvalidEmail = !FieldValidators.email(newFormValue.email.value);
      const emailsDoesntMatch = newFormValue.checkEmail.value.length && newFormValue.checkEmail.value !== newFormValue.email.value;
      const passwordTooShort = newFormValue.password.value.length && newFormValue.password.value.length < 6;

      if (hasInvalidEmail) {
        hasErrors = true;
        newFormValue.email.error = changeEmailLocale.form.errors.invalidEmail;
      }

      if (emailsDoesntMatch) {
        hasErrors = true;
        newFormValue.checkEmail.error = changeEmailLocale.form.errors.emailsDoesntMatch;
      }

      if (passwordTooShort) {
        hasErrors = true;
        newFormValue.password.error = changeEmailLocale.form.errors.passwordTooShort;
      }

      setChangeEmailForm(newFormValue);
      return (hasErrors);
    };

    const disabledSubmit = () => {
      let isDisabled = false;
      const { email, checkEmail, password } = changeEmailForm;

      if (!email.value.length || !checkEmail.value.length || password.value.length < 6) {
        isDisabled = true;
      }

      if (email.error || checkEmail.error || password.error || blockedTime) {
        isDisabled = true;
      }

      return isDisabled;
    };

    return ({
      changeEmailLocale,
      changeEmailForm,
      setChangeEmailForm,
      validateFields,
      email: clientInfo.email,
      disabledSubmit,
      requestEmailChange,
      countDownResend,
      setCountDownResend,
      loadingChange,
      emailVerifyLocale,
      handleGoBack,
      openForm,
      changeFlow,
      timer,
      blockedTime,
    });
  }, [
    changeEmailLocale,
    changeEmailForm,
    clientInfo,
    countDownResend,
    loadingChange,
    emailVerifyLocale,
    requestEmailChange,
    openForm,
    changeFlow,
    handleGoBack,
    timer,
    blockedTime,
  ]);

  const getSentEmailProps = useCallback(() => ({
    sentEmailLocale,
    clientInfo,
    countDownResend,
    emailVerifyLocale,
    setCountDownResend,
    handleResendEmail,
    loadingResend,
    openForm,
    blockedTime,
    timer,
  }), [
    sentEmailLocale,
    clientInfo,
    countDownResend,
    setCountDownResend,
    emailVerifyLocale,
    loadingResend,
    openForm,
    handleResendEmail,
    blockedTime,
    timer,
  ]);

  const steps = {
    [EmailSteps.VALIDATE_EMAIL]: <ValidateEmail {...getValidateEmailProps()} />,
    [EmailSteps.CHANGE_EMAIL]: <ChangeEmail {...getChangeEmailProps()} />,
    [EmailSteps.SENT_EMAIL]: <SentEmail {...getSentEmailProps()} />,
  };

  return (
    <Styles.ContainerWrapper>
      <Styles.Container>
        {(featureToggleEmailNumberFlowSkip || changeFlow) && (
          <Styles.CloseButtonWrapper>
            <IconButton
              icon="IconClose"
              onClick={changeFlow ? handleGoBack : closeEmailFlow}
            />
          </Styles.CloseButtonWrapper>
        )}

        <Styles.LogoWrapper>
          <HgOrangeLogo />
        </Styles.LogoWrapper>

        {steps[currentStep]}
      </Styles.Container>
    </Styles.ContainerWrapper>
  );
};

export default EmailVerify;
